import { Injectable, OnDestroy } from '@angular/core';
import { EdlCommonModalService, HttpErrorMessageHelper } from '@edl-development/edl-common';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { EdlComment } from 'src/app/model/comment.model';
import { CommentState } from 'src/app/model/enum/comment-state.enum';
import { CommentService } from 'src/app/services/comment.service';
import { RequestQueue } from 'src/app/utilities/request-queue/request-queue.util';
import { CommentOptionalFilters } from '../../interfaces/comment-optional-filters.interface';
import { CommentFilters } from '../../interfaces/comment-filters.interface';

@Injectable()
export class ObservationCommentService implements OnDestroy {
    public edlCommentsReplaySubject: BehaviorSubject<EdlComment[]>;
    public edlCommentAttachmentSubject: BehaviorSubject<EdlComment[]>;
    public isInitialized: BehaviorSubject<{ isInitialized: boolean, initialComments: EdlComment[] }>;

    // Request Queue
    private reloadQueue: RequestQueue<EdlComment[]>;

    // Save Queue
    private saveQueue: RequestQueue<EdlComment | null | undefined>;

    // Fetch Comment Trackers
    private reloadRequestCount = 0;
    private lastFetchCommentsRequest: Promise<EdlComment[]> = Promise.resolve([]);
    private edlAllCommentsReplaySubject: BehaviorSubject<EdlComment[]>;

    // Optional Comment Filters
    private _filters: CommentFilters = { allowedUsersIds: [], blockedUserIds: [], canShowHistorical: true, isReadOnly: false };
    public get filters() {
        return {...this._filters};
    }

    private set filters(value: CommentFilters) {
        this._filters = value;
    }

    private observationType?: number;
    private observationId?: string;

    public get isLoading(): boolean {
        return this.reloadQueue.isLoading;
    }

    public get isLoadingObservable(): Observable<boolean> {
        return this.reloadQueue.isLoadingObservable;
    }

    constructor(
        private commentService: CommentService,
        private edlCommonModalService: EdlCommonModalService,
        private toastrService: ToastrService
    ) {
        this.edlAllCommentsReplaySubject = new BehaviorSubject<EdlComment[]>([]);
        this.edlCommentsReplaySubject = new BehaviorSubject<EdlComment[]>([]);
        this.edlCommentAttachmentSubject = new BehaviorSubject<EdlComment[]>([]);
        this.reloadQueue = new RequestQueue<EdlComment[]>([]);
        this.saveQueue = new RequestQueue<EdlComment | null | undefined>();
        this.isInitialized = new BehaviorSubject<{ isInitialized: boolean, initialComments: EdlComment[] }>({ isInitialized: false, initialComments: [] });
    }

    ngOnDestroy(): void {
        this.edlAllCommentsReplaySubject.complete();
        this.edlCommentsReplaySubject.complete();
        this.edlCommentAttachmentSubject.complete();
        this.reloadQueue.complete();
    }

    initialize = (observationType?: number, observationId?: string, optionalFilters: CommentOptionalFilters = {}): Promise<EdlComment[]> => {
        this.observationType = observationType;
        this.observationId = observationId;
        this.updateOptinalFilters(optionalFilters);

        return this.reloadComments()
            .then(results => {
                this.isInitialized.next({ isInitialized: true, initialComments: results });
                return results;
            })
    }

    saveComment = (comment: EdlComment, shouldReload: boolean = false): Promise<EdlComment | null | undefined> => {
        return this.saveQueue.generateAndEnqueRequest(() => firstValueFrom(this.commentService.saveComment(comment)))
            .then((result) => {
                if (shouldReload) this.reloadComments();

                return result;
            })
    }

    reloadComments = (): Promise<EdlComment[]> => {
        if (this.observationType === undefined || this.observationId === undefined) {
            this.onFailedToLoadComments()
            return Promise.reject();
        }

        if (this.reloadRequestCount < 2) {
            this.lastFetchCommentsRequest = this.createReloadRequest(this.observationType, this.observationId, this.filters);
        }

        return this.lastFetchCommentsRequest;
    }

    setOptionalFilters(optionalFilters: CommentOptionalFilters) {
        this.updateOptinalFilters(optionalFilters);
        this.lastFetchCommentsRequest = this.createFilterRequest(this.filters);

        return this.lastFetchCommentsRequest;
    }

    private updateOptinalFilters({ allowedUsersIds, blockedUserIds, canShowHistorical, isReadOnly }: CommentOptionalFilters) {
        this.filters = {
            allowedUsersIds: allowedUsersIds ?? this.filters.allowedUsersIds,
            blockedUserIds: blockedUserIds ?? this.filters.blockedUserIds,
            canShowHistorical: canShowHistorical ?? this.filters.canShowHistorical,
            isReadOnly: isReadOnly ?? this.filters.isReadOnly
        };
    }

    allPublished = (): Promise<boolean> => {
        return this.reloadComments()
            .then((result)=> (result.filter(x => x.commentState == CommentState.DRAFT).length ?? 0) === 0);
    }

    getCommentsByComponent = (component: string): EdlComment[] => {
        return this.edlCommentsReplaySubject.value.filter(c => c.lookupValue === component);
    }

    publishAllDrafts = (title: string): Promise<boolean> => {
        return this.allPublished()
            .then(result => result ? Promise.resolve(true) : this.publishAllDrafts_openModal(title));
    }

    private publishAllDrafts_openModal= (title: string): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            this.edlCommonModalService
                .confirm(title, "Some of your comments are still in a draft state. Are you sure you want to submit? Any comments in a draft state will be automatically published.")
                .then(() => {
                    // If the user presses "Confirm" in the confirm modal
                    firstValueFrom(
                        this.commentService.publishAllComments(this.observationType as number, this.observationId as string)
                    ).then(() => {
                        this.reloadComments();
                        this.toastrService.success('All of your comments have been published.');
                        resolve(true);
                    }).catch((reason: any) => {
                        this.toastrService.error(HttpErrorMessageHelper.getErrorMessage(reason));
                        reject(reason)
                    });
                }).catch(() => {
                    // If user presses "Cancel" in the confirm modal
                    resolve(false)
                });
        });
    }

    private createFilterRequest = (filters: CommentFilters): Promise<EdlComment[]> =>
        this.reloadQueue.generateAndEnqueRequest(
            this.generateFilterCommentRequest(filters)
        )

    private createReloadRequest = (observationType: number, observationId: string, filters: CommentFilters): Promise<EdlComment[]> => {
        this.reloadRequestCount++;
        const action = this.generateFetchCommentRequest(observationType, observationId, filters);
        return this.reloadQueue.generateAndEnqueRequest(action)
            .finally(() => {
                this.reloadRequestCount--;
            });
    }

    private generateFetchCommentRequest = (
        observationType: number,
        observationId: string,
        filters: CommentFilters
    ) => () =>
            firstValueFrom(
                this.commentService.getCommentsForObservation(observationType, observationId)
            ).then((edlComments: EdlComment[]) => {
                this.edlAllCommentsReplaySubject.next(edlComments);
            }).catch((reason: any) => {
                this.onFailedToLoadComments();
                throw reason;
            }).then(() => this.generateFilterCommentRequest(filters)());

    private generateFilterCommentRequest = (
        filters: CommentFilters
    ) => () =>
            firstValueFrom(
                this.edlAllCommentsReplaySubject
            ).then((edlComments: EdlComment[]) => {
                const filteredComments = this.filterComments(edlComments, filters);
                this.edlCommentsReplaySubject.next(filteredComments);
                return filteredComments;
            });

    private filterComments = (comments: EdlComment[], { allowedUsersIds, blockedUserIds, canShowHistorical }: CommentFilters) => {
        return comments.filter(c => allowedUsersIds.length === 0 || allowedUsersIds.includes(c.userId))
            .filter(c => blockedUserIds.length === 0 || !blockedUserIds.includes(c.userId))
            .filter(c => canShowHistorical || !c.historic)
    }

    private onFailedToLoadComments = () => {
        this.toastrService.error("Failed to Load Comments")
    }

   onAttachmentChange = (result:EdlComment) => {
      this.edlCommentAttachmentSubject.next([result]);
    }
}
